import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useBrand } from "../context/BrandContext";
import { selectSalon, getMySalons } from "../api/auth";
import { buildHostUrl } from "../config/hostRouting";
import { getMarketplaceAuthenticatedLandingPath } from "../utils/marketplaceShellRouting";
import { getTechnicianLandingPath } from "../utils/technicianRouting";

export default function SelectSalon() {
  const navigate = useNavigate();
  const location = useLocation();
  const runtimeBrand = useBrand();
  const nextPath = location.state?.next;
  const {
    token,
    user,
    accessibleSalons,
    setAccessibleSalons,
    login,
    refreshTechnicianOnboarding,
  } = useAuth();

  const [salons, setSalons] = useState(accessibleSalons || []);
  const [loading, setLoading] = useState(!salons.length);
  const [error, setError] = useState("");
  const autoSelectAttemptedRef = useRef(false);
  const marketplaceAccessUrl = buildHostUrl(
    runtimeBrand.marketplaceHostName,
    "/account/ellyanna-access"
  );

  useEffect(() => {
    let active = true;
    if (!token || salons.length) {
      return;
    }
    setLoading(true);
    getMySalons(token)
      .then((data) => {
        if (!active) return;
        const list = Array.isArray(data.salons) ? data.salons : [];
        setSalons(list);
        setAccessibleSalons(list);
      })
      .catch((err) => {
        if (!active) return;
        setError(err.message);
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [token, salons.length, setAccessibleSalons]);

  const handleSelect = useCallback(async (salonId) => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const response = await selectSalon(salonId, token);
      let nextPathOverride = nextPath || "/manager/schedule";
      let onboardingData = null;
      const normalizedRole = (response.role || "").toLowerCase();

      if (normalizedRole === "technician") {
        try {
          onboardingData = await refreshTechnicianOnboarding({
            accessToken: response.access_token,
          });
        } catch (statusErr) {
          console.error("Unable to load technician onboarding", statusErr);
        }
        const hasSelectedSalon =
          onboardingData?.has_selected_salon ?? Boolean(response.salon_id);
        if (runtimeBrand.isMarketplaceHost) {
          nextPathOverride =
            nextPath ||
            getMarketplaceAuthenticatedLandingPath({
              role: normalizedRole,
              user: response,
            });
        } else {
          nextPathOverride = getTechnicianLandingPath({
            hasSelectedSalon,
            staffSetupComplete: onboardingData?.staff_setup_complete ?? false,
            profileComplete: onboardingData?.profile_complete ?? false,
          });
        }
      } else if (runtimeBrand.isMarketplaceHost) {
        nextPathOverride =
          nextPath ||
          getMarketplaceAuthenticatedLandingPath({
            role: normalizedRole,
            user: response,
          });
      } else {
        nextPathOverride = nextPath || "/manager/schedule";
      }

      const technicianStatusPayload = onboardingData
        ? {
            profile_complete: onboardingData.profile_complete ?? false,
            staff_setup_complete:
              onboardingData.staff_setup_complete ?? null,
            has_selected_salon:
              onboardingData.has_selected_salon ??
              Boolean(response.salon_id),
            salon_id: onboardingData.salon_id ?? response.salon_id ?? null,
            setup_complete: Boolean(onboardingData.staff_setup_complete ?? false),
          }
        : undefined;

      login(response.access_token, {
        ...response,
        salons: onboardingData?.accessible_salons ?? accessibleSalons,
        technician_status: technicianStatusPayload,
      });
      navigate(nextPathOverride, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [
    token,
    nextPath,
    refreshTechnicianOnboarding,
    login,
    accessibleSalons,
    navigate,
    runtimeBrand.isMarketplaceHost,
  ]);

  useEffect(() => {
    if (!token || loading || autoSelectAttemptedRef.current) {
      return;
    }
    if (salons.length !== 1) {
      return;
    }
    const onlySalon = salons[0] || {};
    const onlySalonId = Number(
      onlySalon.salon_id ?? onlySalon.id ?? onlySalon.salonId ?? null
    );
    if (!Number.isFinite(onlySalonId) || onlySalonId <= 0) {
      return;
    }
    autoSelectAttemptedRef.current = true;
    handleSelect(onlySalonId);
  }, [token, loading, salons, handleSelect]);

  const showMarketplaceAccessRecovery =
    !runtimeBrand.isMarketplaceHost &&
    !loading &&
    salons.length === 0 &&
    !user?.is_super_admin &&
    !user?.salon_id &&
    !user?.has_owner_access &&
    !user?.has_technician_access;

  if (showMarketplaceAccessRecovery) {
    return (
      <div className="min-h-screen bg-[#fefefe] flex flex-col items-center">
        <div className="w-full max-w-4xl px-4 py-12">
          <div className="rounded-[1.75rem] border border-slate-200 bg-white p-8 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
              Ellyanna Access
            </div>
            <h1 className="mt-3 text-3xl font-semibold text-slate-900">
              This account does not have salon access yet
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600">
              You can sign in with this shared login, but Ellyanna Booking needs a real
              salon or business context. Continue on Ellyanna Boothshare to request salon access
              or start your own owner setup.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href={marketplaceAccessUrl}
                className="rounded-full bg-[#2f3566] px-5 py-3 text-sm font-semibold text-white hover:bg-[#252a53]"
              >
                Go To Ellyanna Boothshare Access
              </a>
              <a
                href={buildHostUrl(runtimeBrand.marketplaceHostName, "/search")}
                className="rounded-full border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Search Workspaces
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fefefe] flex flex-col items-center">
      <div className="w-full max-w-4xl px-4 py-12">
        <h1 className="text-3xl font-semibold text-slate-900 mb-3">
          Choose a salon
        </h1>
        <p className="text-sm text-slate-500 mb-6">
          Select the salon you would like to manage for this session. You can
          switch salons at any time from the navbar.
        </p>

        {error && (
          <div className="mb-4 rounded bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 text-sm">
            {error}
          </div>
        )}

        {loading && <p className="text-sm text-slate-500">Loading salons...</p>}

        {!loading && salons.length === 0 && (
          <div className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded p-4">
            No salons found for your account. If you believe this is a mistake,
            please ask your manager to grant you access.
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2 mt-4">
          {salons.map((salon) => {
            const salonIdValue = salon.salon_id ?? salon.id ?? salon.salonId ?? null;
            if (!salonIdValue) return null;
            return (
              <div
                key={salonIdValue}
                className="border border-slate-200 rounded-lg p-5 shadow-sm bg-white hover:shadow-md transition"
              >
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-semibold text-slate-900">
                    {salon.name}
                  </h2>
                  <span className="text-xs font-medium px-2 py-1 rounded-full bg-emerald-100 text-emerald-700">
                    {salon.role || "technician"}
                  </span>
                </div>
                <p className="text-sm text-slate-500">
                  {salon.city ?? "City not provided"}
                </p>
                <p className="text-xs uppercase text-slate-400 tracking-wide mt-2">
                  SALON ID {salonIdValue}
                </p>
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => handleSelect(salonIdValue)}
                  className="mt-4 w-full text-sm font-semibold text-white bg-rose-600 hover:bg-rose-700 py-2 rounded"
                >
                  Continue to {salon.name}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
